import React, {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import { Node } from 'reactflow';
import { CustomTooltip } from '@components/CustomTooltip';
import useStore from '@components/MainStage/store';
import { useProject } from '@context/Project/ProjectProvider';
import useProjectData from '@hooks/useProjectData';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import {
  activeCategoryCount,
  isCiaCategory,
  useModelFilters,
} from '@store/zustand/model-filters';
import { themePalette } from '@theme/muiTheme';
import { SCOPE_FILTER_UNASSIGNED } from '@utils/domains';
import { LockIcon } from '@utils/iconsDefs';
import { countNodesByValue } from '@utils/modelFilterCounts';
import {
  StyledFilterButton,
  StyledFilterIcon,
} from '@views/Project/components/Filters/Filters.styled';
import { CATEGORY_ICONS } from '@views/Project/components/ModelFiltersPopover/categoryIcons';
import FilterCategoryPanel from '@views/Project/components/ModelFiltersPopover/FilterCategoryPanel';
import FilterCisControlPanel from '@views/Project/components/ModelFiltersPopover/FilterCisControlPanel';
import FilterCisPanel from '@views/Project/components/ModelFiltersPopover/FilterCisPanel';
import FilterCriteriaPanel from '@views/Project/components/ModelFiltersPopover/FilterCriteriaPanel';
import FilterScopeDomainPanel from '@views/Project/components/ModelFiltersPopover/FilterScopeDomainPanel';
import FilterScopePanel from '@views/Project/components/ModelFiltersPopover/FilterScopePanel';
import {
  FILTERS_POPOVER_OWNER_ATTRIBUTE,
  filtersPopoverOwnerProps,
  useFiltersPopover,
  useFiltersPopoverActions,
} from '@views/Project/components/ModelFiltersPopover/filtersPopover.store';
import FiltersRootPanel from '@views/Project/components/ModelFiltersPopover/FiltersRootPanel';
import { isShareFiltersOffered } from '@views/Project/components/ModelFiltersPopover/shareFiltersAvailability';
import { useCategoryOptions } from '@views/Project/components/ModelFiltersPopover/useCategoryOptions';
import { useSetScopeType } from '@views/Project/components/ModelFiltersPopover/useSetScopeType';

const NO_NODES: Node[] = [];

const PANEL_WIDTH = 340;

const PANEL_GAP = 8;

/**
 * One panel's card: every panel the popover shows — the root, a category,
 * a criteria list, or the values beside that list — sits in a paper of this
 * shape, so the side panel is drawn exactly like the one it opens from.
 */
const PANEL_PAPER_SX: SxProps<Theme> = {
  width: PANEL_WIDTH,
  borderRadius: '8px',
  overflow: 'hidden',
  bgcolor: themePalette.white,
  boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.10)',
};

export const LOCKED_FILTERS_TOOLTIP =
  "Filters are locked while you're inside this insight breakdown. They still apply to the results — go back to change them.";

type Props = {
  isLocked?: boolean;
};

/**
 * The Filters funnel and its popover: the only entry point for model
 * filtering.
 *
 * Built on `Popper` inside a `ClickAwayListener`, not the Modal-backed
 * `Popover` every other popover in this repo uses: AC6.2 needs a click
 * outside to reach the element it lands on, and a Modal backdrop swallows that
 * first click. The listener wraps the trigger as well, so a click on the
 * funnel counts as inside and only `toggle` handles it (AC2.1) instead of a
 * close followed by a reopen; it fires on pointer-down, so the popover is gone
 * before the click it precedes is delivered. Without a Modal there is no free
 * Escape handling either, so while open a capture-phase `keydown` listener on
 * the document closes the popover and stops the event before the canvas's
 * bubble-phase Escape handlers see it (AC6.3). Both dismissals pause while the
 * unsaved-changes prompt is up: a tick that hides a dirty drawer's entity
 * opens `ConfirmDialog`, which is portaled outside the popover's DOM and React
 * trees, so the pointer-down that answers it would otherwise read as a click
 * away, and the Escape that cancels it would close the popover instead of
 * reaching the dialog (AC4.3).
 *
 * Root, criteria and values panels share one anchor and one popover. A CIA
 * category with one criteria opens straight onto that criteria's values,
 * under the criteria's name, since that name is the category's; with
 * several it opens its criteria list, and a criteria row opens that
 * criteria's values in a second card beside the list: the same popover and
 * the same dismissal, but its own paper, 8px to the right and top-aligned
 * at its own height, which is how the design draws the pair. One side
 * panel at a time: another row replaces it, the open row closes it, and so
 * does its own back, which leaves the list showing; back on the list
 * returns to the root and drops it, so a later open from the root, a chip
 * or the +N row lands on the list alone — every path that opens a category
 * resets the criteria id. The shape is resolved while rendering rather than
 * when the category is chosen, so whoever opens a category needs no
 * knowledge of its criteria. Closing from any level resets to the root for
 * the next open; the selection lives in the store and dismissal never
 * touches it (AC6.1).
 *
 * CIS-18 reference is the same arrangement with the Controls list in the
 * place of the criteria list: a Control row opens that Control's values
 * in the second paper, one at a time, closed by its row, its back, a pill
 * switch or the start of a search. Where the panel stands — the pill, the
 * open Control, the search query — lives in the popover store with the
 * criteria id, so the root row lands it on IG1 and a chip or +N row on
 * Controls, with the selection's badges on the rows and no Control open.
 *
 * Scope is the same arrangement once more: Enterprise wide and Unassigned
 * are values on the category panel, each Domain a navigation row whose
 * values — the Domain and its Sub-domains — open in the second paper, one
 * at a time, closed by its row or its back, with the open Domain kept in
 * the popover store. The Scope type selector lives in the category panel's
 * header only. A switch goes through `useSetScopeType`, which drops the ids
 * no entity of this mode holds under the new type, Enterprise wide and
 * Unassigned excepted, and Scope's counts are taken under the current type.
 *
 * Open state lives in `filtersPopover.store` rather than here so an
 * applied-filter chip, or a row in the +N popover, can open it straight onto
 * a category and anchored to themselves. Elements marked with the owner
 * attribute are exempt from click-away for the same reason: they re-target
 * the popover in the same gesture that would otherwise dismiss it. When the
 * anchor leaves the document — a chip cleared from its own panel — the popover
 * re-anchors to the funnel instead of closing, so a change made inside it
 * never closes it; the funnel registers itself with the store as that fallback
 * so the chip row can fall back to it too when a chip unmounts in a render
 * this component took no part in. It registers through a callback ref rather
 * than from a mount effect because the button remounts whenever the lock
 * toggles — `CustomTooltip` with `skip` swaps the element type above it — and
 * a registration made once would keep naming the detached first button; the
 * ref is called with the new node on every remount and with null on unmount.
 * Unmounting closes it: the store is a module singleton that
 * nothing else resets, and a navigation while open (browser Back, a session
 * expiry redirect) reaches no dismissal, so without this the next toolbox to
 * mount would reopen on the previous project's panel, re-anchored to its own
 * funnel.
 *
 * Counts are computed only while the popover is open with nothing selected
 * anywhere (AC5.2), and the canvas nodes are subscribed to only then, so the
 * toolbox does not re-render on every node change while the popover is shut.
 *
 * Locked, the funnel stays in the toolbar dimmed, with a lock glyph and the
 * badge for whatever selection still applies, but opens nothing: the click is
 * ignored, and the popover closes if it was open when the lock arrived. It is
 * marked `aria-disabled` rather than `disabled` because a disabled button
 * emits no pointer or focus events, and the lock tooltip has to show on hover
 * and on keyboard focus alike. Whether the funnel is locked is the toolbar's
 * decision, as is whether it is rendered at all.
 *
 * Whether the root header offers the Share filters pill is the tier's call:
 * it is shown only when the subscription has both Business alignment and
 * Controls, read here from the project so the root panel fetches nothing.
 */
const ModelFiltersPopover: FC<Props> = ({ isLocked = false }) => {
  const {
    toolbox: { mode },
    isUnsavedModalOpen,
  } = useProject();

  const {
    anchorEl,
    category,
    criteriaId,
    cisPill,
    cisControlId,
    cisQuery,
    scopeDomainId,
  } = useFiltersPopover();
  const {
    openRoot,
    showCategory,
    showRoot,
    setCriteriaId,
    setCisPill,
    setCisControlId,
    setCisQuery,
    setScopeDomainId,
    setFallbackAnchor,
    reanchorIfDetached,
    close,
  } = useFiltersPopoverActions();
  const registerTrigger = useCallback(
    (node: HTMLButtonElement | null) => setFallbackAnchor(node),
    [setFallbackAnchor],
  );

  const state = useModelFilters();
  const activeCount = activeCategoryCount(state);
  const {
    options,
    criteria,
    cisCatalogue,
    scope,
    titles,
    labels,
    availability,
    vocabulary,
  } = useCategoryOptions();
  const [{ subscription }] = useProjectData();
  const canShareFilters = isShareFiltersOffered(subscription?.modes);

  const isOpen = Boolean(anchorEl);
  const ciaCategory = category && isCiaCategory(category) ? category : null;
  const ciaCriteria = ciaCategory ? criteria[ciaCategory] ?? [] : [];
  const activeCriteria =
    ciaCriteria.length > 1
      ? ciaCriteria.find(({ id }) => id === criteriaId)
      : undefined;
  const activeControl =
    category === 'cis' && cisControlId
      ? cisCatalogue.controls.find(({ id }) => id === cisControlId)
      : undefined;
  const activeScopeDomain =
    category === 'domains' && scopeDomainId
      ? scope.domains.find(({ id }) => id === scopeDomainId)
      : undefined;
  const scopeAlwaysKept = useMemo(
    () => [SCOPE_FILTER_UNASSIGNED, ...(scope.wide ? [scope.wide.id] : [])],
    [scope],
  );
  const setScopeType = useSetScopeType(mode, scopeAlwaysKept);
  const needsCounts = isOpen && activeCount === 0 && mode !== null;
  const nodes = useStore((store) => (needsCounts ? store.nodes : NO_NODES));

  const counts = useMemo(
    () =>
      needsCounts && mode
        ? countNodesByValue(nodes, mode, vocabulary, state.scopeType)
        : undefined,
    [needsCounts, mode, nodes, vocabulary, state.scopeType],
  );

  useLayoutEffect(() => {
    reanchorIfDetached();
  });

  useEffect(() => close, [close]);

  useEffect(() => {
    if (isLocked) close();
  }, [isLocked, close]);

  const isDismissible = isOpen && !isUnsavedModalOpen;

  useEffect(() => {
    if (!isDismissible) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.stopPropagation();
      close();
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isDismissible, close]);

  const toggle = (event: MouseEvent<HTMLElement>) => {
    if (isLocked) return;

    if (isOpen) {
      close();

      return;
    }

    openRoot(event.currentTarget);
  };

  const handleClickAway = (event: globalThis.MouseEvent | TouchEvent) => {
    const { target } = event;

    if (
      target instanceof Element &&
      target.closest(`[${FILTERS_POPOVER_OWNER_ATTRIBUTE}]`)
    ) {
      return;
    }

    close();
  };

  const toggleCriteria = (id: string) =>
    setCriteriaId(id === criteriaId ? undefined : id);

  const closeSidePanel = () => setCriteriaId(undefined);

  const toggleControl = (id: string) =>
    setCisControlId(id === cisControlId ? undefined : id);

  const closeControlPanel = () => setCisControlId(undefined);

  const toggleScopeDomain = (id: string) =>
    setScopeDomainId(id === scopeDomainId ? undefined : id);

  const closeScopeDomainPanel = () => setScopeDomainId(undefined);

  const renderPanel = () => {
    if (!category) {
      return (
        <FiltersRootPanel
          onOpenCategory={showCategory}
          availability={availability}
          titles={titles}
          labels={labels}
          icons={CATEGORY_ICONS}
          canShareFilters={canShareFilters}
        />
      );
    }

    if (category === 'domains') {
      return (
        <FilterScopePanel
          tree={scope}
          scopeType={state.scopeType}
          counts={counts?.domains}
          activeDomainId={activeScopeDomain?.id}
          onScopeTypeChange={setScopeType}
          onOpenDomain={toggleScopeDomain}
          onBack={showRoot}
        />
      );
    }

    if (category === 'cis') {
      return (
        <FilterCisPanel
          catalogue={cisCatalogue}
          counts={counts?.cis}
          pill={cisPill}
          activeControlId={activeControl?.id}
          query={cisQuery}
          onPillChange={setCisPill}
          onOpenControl={toggleControl}
          onQueryChange={setCisQuery}
          onBack={showRoot}
        />
      );
    }

    if (!isCiaCategory(category)) {
      return (
        <FilterCategoryPanel
          category={category}
          options={options[category] ?? []}
          counts={counts?.[category]}
          onBack={showRoot}
        />
      );
    }

    if (ciaCriteria.length === 1) {
      const [only] = ciaCriteria;

      return (
        <FilterCategoryPanel
          category={category}
          title={only.name}
          options={only.options}
          counts={counts?.[category]?.[only.id]}
          criteriaId={only.id}
          onBack={showRoot}
        />
      );
    }

    return (
      <FilterCriteriaPanel
        category={category}
        criteria={ciaCriteria}
        activeCriteriaId={activeCriteria?.id}
        onOpenCriteria={toggleCriteria}
        onBack={showRoot}
      />
    );
  };

  return (
    <ClickAwayListener
      onClickAway={handleClickAway}
      mouseEvent={isDismissible ? 'onPointerDown' : false}
      touchEvent={isDismissible ? 'onTouchStart' : false}
    >
      <Box position="relative" display="flex" alignItems="center">
        <CustomTooltip
          skip={!isLocked}
          title={LOCKED_FILTERS_TOOLTIP}
          placement="bottom-start"
        >
          <StyledFilterButton
            ref={registerTrigger}
            $isActive={isOpen}
            $isDisabled={isLocked}
            aria-disabled={isLocked || undefined}
            onClick={toggle}
            aria-label="Filters"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            {...filtersPopoverOwnerProps}
          >
            <StyledFilterIcon fill={themePalette.grey[isLocked ? 700 : 900]} />

            {isLocked && (
              <Box
                component="span"
                data-testid="model-filters-lock"
                aria-hidden
                sx={{
                  position: 'absolute',
                  right: -6,
                  bottom: -6,
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: themePalette.white,
                }}
              >
                <LockIcon
                  width={12}
                  height={12}
                  fill={themePalette.grey[700]}
                />
              </Box>
            )}

            {activeCount > 0 && (
              <Stack
                data-testid="model-filters-badge"
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '-50%',
                  transform: 'translate(-50%, -50%)',
                  bgcolor: themePalette.primary.main,
                  width: 20,
                  height: 20,
                  borderRadius: '50px',
                }}
              >
                <Typography variant="body2" color="grey.1000">
                  {activeCount}
                </Typography>
              </Stack>
            )}
          </StyledFilterButton>
        </CustomTooltip>

        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <Stack
            role="dialog"
            aria-label="Filters"
            direction="row"
            alignItems="flex-start"
            gap={`${PANEL_GAP}px`}
            sx={{ mt: '8px' }}
          >
            <Paper elevation={0} sx={PANEL_PAPER_SX}>
              {renderPanel()}
            </Paper>

            {ciaCategory && activeCriteria && (
              <Paper elevation={0} sx={PANEL_PAPER_SX}>
                <FilterCategoryPanel
                  category={ciaCategory}
                  title={activeCriteria.name}
                  options={activeCriteria.options}
                  counts={counts?.[ciaCategory]?.[activeCriteria.id]}
                  criteriaId={activeCriteria.id}
                  onBack={closeSidePanel}
                />
              </Paper>
            )}

            {activeControl && (
              <Paper elevation={0} sx={PANEL_PAPER_SX}>
                <FilterCisControlPanel
                  control={activeControl}
                  counts={counts?.cis}
                  onBack={closeControlPanel}
                />
              </Paper>
            )}

            {activeScopeDomain && (
              <Paper elevation={0} sx={PANEL_PAPER_SX}>
                <FilterScopeDomainPanel
                  domain={activeScopeDomain}
                  counts={counts?.domains}
                  onBack={closeScopeDomainPanel}
                />
              </Paper>
            )}
          </Stack>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ModelFiltersPopover;
